import React, { CSSProperties, useMemo } from 'react';
import './StudioBoard.css';
import { StudioIcon } from './StudioIcons';
import { BOARD_TEXT, getBoardSections, Locale } from '../studioLocale';

interface Props {
  elements: any[];
  connections: any[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  locale: Locale;
}

const norm = (type: string) => {
  if (type === 'world') {
    return 'world_state';
  }
  if (type === 'device') {
    return 'plot_device';
  }
  return type;
};

const displayName = (item: any): string => {
  const candidate = item?.name ?? item?.data?.name ?? item?.data?.title ?? item?.id ?? '';
  if (typeof candidate === 'string' || typeof candidate === 'number') {
    const text = String(candidate).trim();
    return text || String(item?.id || '');
  }
  if (candidate && typeof candidate === 'object') {
    const nested = (candidate as Record<string, unknown>).name;
    if (typeof nested === 'string' && nested.trim()) {
      return nested.trim();
    }
  }
  return String(item?.id || '');
};

const StudioBoard: React.FC<Props> = ({ elements, connections, selectedId, onSelect, locale }) => {
  const text = BOARD_TEXT[locale];
  const sections = useMemo(
    () =>
      getBoardSections(locale).map(({ type, label, note, icon, accent, tint }) => ({
        type,
        label,
        note,
        icon,
        accent,
        tint,
        items: elements.filter((item) => norm(item.type) === type),
      })),
    [elements, locale]
  );

  const map = useMemo(() => {
    const out = new Map<string, any[]>();
    connections.forEach((connection) => {
      out.set(connection.sourceId, [...(out.get(connection.sourceId) || []), connection]);
      out.set(connection.targetId, [...(out.get(connection.targetId) || []), connection]);
    });
    return out;
  }, [connections]);

  if (!elements.length) {
    return (
      <div className="studio-board studio-board--empty">
        <div className="studio-board__empty-card">
          <span className="studio-board__empty-icon">
            <StudioIcon name="quill" />
          </span>
          <strong>{text.emptyTitle}</strong>
          <p>{text.emptyBody}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="studio-board">
      {sections.map((section) => (
        <section
          key={section.type}
          className="studio-column"
          style={
            {
              '--studio-accent': section.accent,
              '--studio-tint': section.tint,
            } as CSSProperties
          }
        >
          <div className="studio-column__header">
            <div className="studio-column__header-main">
              <span className="studio-column__icon">
                <StudioIcon name={section.icon} />
              </span>
              <div>
                <h3>{section.label}</h3>
                <p>{section.note}</p>
              </div>
            </div>
            <span>{section.items.length}</span>
          </div>

          <div className="studio-column__list">
            {!section.items.length && <div className="studio-column__empty">{text.emptySection}</div>}

            {section.items.map((item) => {
              const detail =
                item.data?.description ||
                item.data?.purpose ||
                item.data?.note ||
                item.data?.motivation ||
                item.data?.premise ||
                '';
              const flag = item.data?.act || item.data?.beat || item.data?.attached_beat || '';

              return (
                <button
                  key={item.id}
                  type="button"
                  className={`studio-card ${selectedId === item.id ? 'is-selected' : ''}`}
                  onClick={() => onSelect(item.id)}
                >
                  <div className="studio-card__top">
                    <strong>{displayName(item)}</strong>
                    <span>{text.relationCount((map.get(item.id) || []).length)}</span>
                  </div>
                  {flag && <div className="studio-card__tag">{String(flag)}</div>}
                  <p>{String(detail || text.fallbackDetail).slice(0, 120)}</p>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

export default StudioBoard;
